import Chip from 'core-system/Chip'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import moment from 'moment'
import type { BenefitHistoryMonth } from 'redux/employee/employeeTypes'
import type { QtfPerEmployeeAllocation } from 'redux/qtf/qtfTypes'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useDrawerTableOperations } from './useDrawerTableOperations'
import {
  ALLOCATION_HISTORY_SEARCH_KEYS,
  allocationHistoryFilters,
  buildAllocationHistoryRows,
  buildCurrentAllocations,
  employerSubsidyCaption,
  formatAllocationMoney,
  type AllocationBalances,
  type AllocationHistoryStatus,
} from './allocationRows'

const Section = styled.div`
  margin-bottom: 1.5rem;
`

const SectionHeader = styled.div`
  margin-bottom: 0.75rem;
`

const Bucket = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const Dot = styled.span<{ $color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
`

const HeaderCell = ({ text }: { text: string }) => (
  <TableRowCell noWrap>
    <Text variant='action4' fontWeight={600}>
      {text}
    </Text>
  </TableRowCell>
)

const MoneyCell = ({ cents }: { cents: number | null }) => (
  <TableRowCell noWrap>
    <Text variant='action3'>{formatAllocationMoney(cents)}</Text>
  </TableRowCell>
)

const statusVariant = (status: AllocationHistoryStatus) => {
  if (status === 'Paid out') {
    return 'green' as const
  }
  if (status === 'Projected') {
    return 'yellow' as const
  }
  return 'grey' as const
}

const bucketColor = {
  transit: palette.secondary.purple500,
  parking: palette.accent.blue500,
}

interface CommuterAllocationsProps {
  allocation?: QtfPerEmployeeAllocation | null
  qtfBalanceData?: AllocationBalances | null
  benefitHistory?: BenefitHistoryMonth[] | null
}

const CommuterAllocations = ({
  allocation,
  qtfBalanceData,
  benefitHistory,
}: CommuterAllocationsProps) => {
  const currentMonth = moment().format('YYYY-MM')
  const currentRows = buildCurrentAllocations(
    allocation?.deductions,
    qtfBalanceData
  )
  const historyRows = useMemo(
    () => buildAllocationHistoryRows(benefitHistory || [], currentMonth),
    [benefitHistory, currentMonth]
  )
  const { apply, bar } = useDrawerTableOperations({
    source: historyRows,
    searchKeys: ALLOCATION_HISTORY_SEARCH_KEYS,
    sortKey: 'monthKey',
    buildFilters: allocationHistoryFilters,
  })
  const visibleHistory = apply(historyRows)

  return (
    <>
      <Section>
        <SectionHeader>
          <Text variant='h5'>Current allocations</Text>
        </SectionHeader>
        <Table
          maxRows={2}
          showPagination={false}
          headerCells={[
            <TableRow key='current-header'>
              <HeaderCell text='Bucket' />
              <HeaderCell text='Employee pre-tax / mo' />
              <HeaderCell text='Employer subsidy / mo' />
              <HeaderCell text='Balance' />
            </TableRow>,
          ]}
          rows={currentRows.map((row) => (
            <TableRow key={row.key}>
              <TableRowCell>
                <Bucket>
                  <Dot $color={bucketColor[row.key]} />
                  <Text variant='action3'>{row.label}</Text>
                </Bucket>
              </TableRowCell>
              <MoneyCell cents={row.employeeDeduction} />
              <MoneyCell cents={row.employerContribution} />
              <MoneyCell cents={row.balance} />
            </TableRow>
          ))}
        />
      </Section>
      <Section>
        <SectionHeader>
          <Text variant='h5'>Allocation history</Text>
          <Text variant='body2' textColor={palette.text.secondary}>
            Includes employer subsidies
          </Text>
        </SectionHeader>
        {bar}
        <Table
          maxRows={5}
          emptyMessage={
            visibleHistory.length === 0 && historyRows.length > 0
              ? 'No matching allocation history'
              : 'No allocation history'
          }
          headerCells={[
            <TableRow key='history-header'>
              <HeaderCell text='Month' />
              <HeaderCell text='Transit' />
              <HeaderCell text='Parking' />
              <HeaderCell text='Employer subsidy' />
              <HeaderCell text='Status' />
            </TableRow>,
          ]}
          rows={visibleHistory.map((row) => {
            const caption = employerSubsidyCaption(row.entry)
            return (
              <TableRow key={row.id}>
                <TableRowCell noWrap>
                  <Text variant='action3'>{row.month}</Text>
                </TableRowCell>
                <MoneyCell cents={row.entry.transitEmployeeDeduction ?? null} />
                <MoneyCell cents={row.entry.parkingEmployeeDeduction ?? null} />
                <TableRowCell>
                  <Text variant='action3'>{row.employerSubsidy}</Text>
                  {caption ? (
                    <Text variant='body2' textColor={palette.text.secondary}>
                      {caption}
                    </Text>
                  ) : null}
                </TableRowCell>
                <TableRowCell>
                  <Chip variant={statusVariant(row.status)}>{row.status}</Chip>
                </TableRowCell>
              </TableRow>
            )
          })}
        />
      </Section>
    </>
  )
}

export default CommuterAllocations
